import { Component, inject, signal, computed, effect, input, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MultiSelectModule } from 'primeng/multiselect';
import { ClientService, ClientBranch, ClientBranchDto } from '../../../services/client';
import { RoMasterService, RoMaster } from '../../../services/ro-master';
import { ToastService } from '../../../services/toast';

@Component({
  selector: 'app-client-branches',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    ToggleSwitchModule,
    MultiSelectModule,
  ],
  templateUrl: './client-branches.html',
  styleUrls: ['../clients-shared.css'],
})
export class ClientBranchesTab {
  clientId = input.required<number>();

  private fb = inject(FormBuilder);
  private svc = inject(ClientService);
  private roSvc = inject(RoMasterService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  rows = signal<ClientBranch[]>([]);

  /** This client's ROs (options for the branch RO multi-select). */
  clientRos = signal<RoMaster[]>([]);
  roOptions = computed(() =>
    this.clientRos().map((r) => ({
      label: `${r.roCode} — ${r.roName}` + (r.masterPolicyNumber ? ` (${r.masterPolicyNumber})` : ''),
      value: r.id,
    })),
  );

  form: FormGroup = this.fb.group({
    id: [0],
    branchCode: ['', Validators.required],
    branchName: ['', Validators.required],
    contactPerson: [''],
    email: ['', Validators.email],
    mobile: ['', Validators.pattern(/^\d{10}$/)],
    address: [''],
    city: [''],
    state: [''],
    country: [''],
    pincode: ['', Validators.pattern(/^\d{6}$/)],
    gstNo: ['', Validators.pattern(/^[0-9A-Z]{15}$/)],
    isDefault: [false],
    isActive: [true],
    // At least one regional office is mandatory when creating/editing a branch.
    roIds: [[] as number[], Validators.required],
  });

  constructor() {
    // load() reads clientId(), so this effect reloads whenever the bound client changes —
    // navigating /clients/12 → /clients/34 swaps the input without recreating the tab
    // component, so a one-shot ngOnInit load would keep showing the previous client's rows.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.load();
      this.loadRos();
    });
  }

  load() {
    this.loading.set(true);
    this.svc.brGetByClient(this.clientId()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  /** Load this client's ROs for the branch RO multi-select. */
  private loadRos() {
    this.roSvc.getAll(this.clientId()).subscribe({
      next: (res: any) => this.clientRos.set(Array.isArray(res?.data) ? res.data : []),
    });
  }

  upper(controlName: string) {
    const ctrl = this.form.get(controlName);
    if (!ctrl) return;
    const v = (ctrl.value ?? '').toString().toUpperCase();
    if (v !== ctrl.value) ctrl.setValue(v, { emitEvent: false });
  }

  // ===================== add / edit =====================
  openAdd() {
    this.editing.set(false);
    this.form.reset({
      id: 0,
      branchCode: '',
      branchName: '',
      contactPerson: '',
      email: '',
      mobile: '',
      address: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      gstNo: '',
      isDefault: this.rows().length === 0, // first branch defaults to default
      isActive: true,
      roIds: [],
    });
    this.showModal.set(true);
  }

  openEdit(b: ClientBranch) {
    this.editing.set(true);
    this.form.reset({
      id: b.id,
      branchCode: b.branchCode ?? '',
      branchName: b.branchName ?? '',
      contactPerson: b.contactPerson ?? '',
      email: b.email ?? '',
      mobile: b.mobile ?? '',
      address: b.address ?? '',
      city: b.city ?? '',
      state: b.state ?? '',
      country: b.country ?? '',
      pincode: b.pincode ?? '',
      gstNo: b.gstNo ?? '',
      isDefault: !!b.isDefault,
      isActive: b.isActive ?? true,
      roIds: [],
    });
    // Load the ROs already mapped to this branch and preselect them.
    this.svc.brRoGetByBranch(b.id).subscribe({
      next: (res: any) => {
        const ids = (res?.data ?? []).map((x: any) => x.RoId ?? x.roId).filter((x: any) => x != null);
        this.form.get('roIds')?.setValue(ids);
      },
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('branchCode')?.invalid) this.toast.warning('Branch code is required.');
      else if (this.form.get('branchName')?.invalid) this.toast.warning('Branch name is required.');
      else if (this.form.get('roIds')?.invalid) this.toast.warning('Select at least one regional office (RO) for this branch.');
      else this.toast.warning('Please fix the highlighted fields.');
      return;
    }

    const v = this.form.getRawValue();
    const dto: ClientBranchDto = {
      id: this.editing() ? Number(v.id) : 0,
      clientId: this.clientId(),
      branchCode: (v.branchCode ?? '').toString().trim().toUpperCase(),
      branchName: (v.branchName ?? '').toString().trim(),
      contactPerson: this.nz(v.contactPerson),
      email: this.nz(v.email),
      mobile: this.nz(v.mobile),
      address: this.nz(v.address),
      city: this.nz(v.city),
      state: this.nz(v.state),
      country: this.nz(v.country),
      pincode: this.nz(v.pincode),
      gstNo: this.nz(v.gstNo),
      isDefault: !!v.isDefault,
      isActive: !!v.isActive,
    } as ClientBranchDto;

    const roIds = (v.roIds ?? []) as number[];

    this.saving.set(true);
    this.svc.brSave(dto).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.saving.set(false);
          if (res?.statusCode === 409) this.toast.error('Branch code already exists for this client');
          else this.toast.error(res?.message || 'Failed to save branch');
          return;
        }
        // Branch saved — now persist its RO set (insert returns { id }, update reuses the form id).
        const branchId = this.editing() ? Number(v.id) : (res?.data?.id ?? res?.data?.Id ?? 0);
        const finish = () => {
          this.saving.set(false);
          this.toast.success(res?.message || (this.editing() ? 'Branch updated' : 'Branch created'));
          this.closeModal();
          this.load(); // reload so a flipped default reflects on the previous default row
        };
        if (branchId) {
          this.svc.brRoSet(branchId, roIds).subscribe({
            next: () => finish(),
            error: () => {
              this.saving.set(false);
              this.toast.error('Branch saved, but failed to update regional offices.');
              this.closeModal();
              this.load();
            },
          });
        } else {
          finish();
        }
      },
      error: (err: any) => {
        this.saving.set(false);
        if (err?.status === 409) this.toast.error('Branch code already exists for this client');
        else this.toast.error(err?.error?.message || 'Failed to save branch');
      },
    });
  }

  // ===================== row actions =====================
  /** Promote a branch to the client's default (backend clears the flag on the others). */
  makeDefault(b: ClientBranch) {
    if (b.isDefault) return;
    const dto: ClientBranchDto = {
      id: b.id,
      clientId: this.clientId(),
      branchCode: b.branchCode,
      branchName: b.branchName,
      contactPerson: b.contactPerson ?? null,
      email: b.email ?? null,
      mobile: b.mobile ?? null,
      address: b.address ?? null,
      city: b.city ?? null,
      state: b.state ?? null,
      country: b.country ?? null,
      pincode: b.pincode ?? null,
      gstNo: b.gstNo ?? null,
      isDefault: true,
      isActive: b.isActive,
    } as ClientBranchDto;
    this.svc.brSave(dto).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to set default');
          return;
        }
        this.toast.success(`"${b.branchName}" is now the default branch`);
        this.load();
      },
    });
  }

  toggleActive(b: ClientBranch) {
    const next = !b.isActive;
    this.patchRow(b.id, { isActive: next });
    this.svc.brSetActive(b.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(b.id, { isActive: b.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(b.id, { isActive: b.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  async remove(b: ClientBranch) {
    if (b.isDefault) {
      const proceed = await this.toast.confirm(
        'This is the default branch — at least one default branch is recommended. Delete anyway?',
        'Delete default branch?',
        'Delete',
      );
      if (!proceed) return;
    } else {
      const ok = await this.toast.confirm(
        `Branch "${b.branchName}" (${b.branchCode}) will be deleted.`,
        'Delete branch?',
        'Delete',
      );
      if (!ok) return;
    }
    this.svc.brDelete(b.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete branch');
          return;
        }
        this.toast.success(res?.message || 'Branch deleted');
        this.load();
      },
    });
  }

  // ===================== utils =====================
  private patchRow(id: number, patch: Partial<ClientBranch>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  private nz(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }
}
